import { Flags } from "@/feature-flags";
import { useOnaEnabledForOrg } from "@/hooks/use-ona-enabled";
import { useGetAccount } from "@/queries/account-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useFeatureFlag, type SettingTypeOf, type SettingValue } from "configcat-react";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export function useFlag<T extends SettingValue>(
    flagKey: string,
    defaultValue: T,
): { value: SettingTypeOf<T>; loading: boolean } {
    const { data: account } = useGetAccount();
    const { data: organization } = useOrganization();

    const { value, loading } = useFeatureFlag<T>(
        flagKey,
        defaultValue,
        account
            ? {
                  custom: {
                      organization_id: organization?.id ?? "",
                  },
                  identifier: account.id,
                  email: account.email,
              }
            : undefined,
    );

    return { value, loading };
}

// Helper methods to OR feature flags and useOnaEnabled
// TODO(gpl): Team Agent plans to consolidate these soon
export const useFlagOnaAIEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.OnaAIEnabled, false);
    const { value: tierEnabled, loading: tierLoading } = useOnaEnabledForOrg();

    return {
        value: flagEnabled || tierEnabled,
        loading: flagLoading || tierLoading,
    };
};

export const useFlagOnaEnvironmentOnboardingEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.OnaEnvironmentOnboardingEnabled, false);
    const { value: tierEnabled, loading: tierLoading } = useOnaEnabledForOrg();

    return {
        value: flagEnabled || tierEnabled,
        loading: flagLoading || tierLoading,
    };
};

export const useFlagIsSentryEnabled = () => {
    return useFlag(Flags.IsSentryEnabled, false);
};

export const useFlagAgentFollowModeEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.EnableAgentFollowMode, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export enum WorkflowAccessLevel {
    None = "none",
    Teaser = "teaser",
    Full = "full",
}

export const useFlagWorkflowsEnabled = () => {
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.WorkflowsEnabled, false);

    let accessLevel: WorkflowAccessLevel = WorkflowAccessLevel.None;
    if (flagEnabled === true) {
        accessLevel = WorkflowAccessLevel.Full;
    } else if (organization?.tier === OrganizationTier.FREE_ONA || organization?.tier === OrganizationTier.CORE) {
        accessLevel = WorkflowAccessLevel.Teaser;
    }

    return {
        accessLevel: accessLevel,
        loading: flagLoading || isPendingOrganization,
    };
};

export const useFlagIsMobileVscodeEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.IsMobileVscodeEnabled, true);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagMultiDomainSSOEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.MultiDomainSSOEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagAutomationsStepReportEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.AutomationsStepReportEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagAutomationsRepoListModeEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.AutomationsRepoListModeEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagSecurityAgentUI = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.SecurityAgentUI, false);
    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagPrebuildsJetbrainsWarmupEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.EnablePrebuildsJetbrainsWarmup, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagSettingsSplitEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.SettingsSplitEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagReviewCommentsEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.ReviewCommentsEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagReusableWebhooksEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.ReusableWebhooksEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useSystemPortsExcludedFromPolicy = () => {
    const { value: exclusions, loading } = useFlag(Flags.SystemPortExclusions, "none");

    return {
        value: exclusions !== "" && exclusions !== "none",
        loading,
    };
};

export const useFlagOrgRolesEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.OrgRolesEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};

export const useFlagServiceAccountSecretsManagementEnabled = () => {
    const { value: flagEnabled, loading: flagLoading } = useFlag(Flags.ServiceAccountSecretsManagementEnabled, false);

    return {
        value: flagEnabled,
        loading: flagLoading,
    };
};
